/**
 * Chunked / streaming reader for the <ClinicalData> section of a CDISC ODM 1.3
 * export (REDCap flavour).
 *
 * A full REDCap ODM snapshot can be tens of megabytes; holding it as a single
 * DOM tree is neither fast nor memory-safe in a browser tab. This reader slices
 * the File, keeps a small rolling text buffer, and hands back one
 * <SubjectData> block at a time — each block is parsed on its own, then
 * discarded.
 *
 * Pure browser APIs (File.slice + DOMParser). No Supabase, no React.
 */

export interface OdmItemValue {
  itemOid: string;
  value: string;
}

export interface OdmFormInstance {
  formOid: string;
  /** ODM FormRepeatKey when the form is repeating (REDCap repeat instance). */
  repeatKey?: string;
  /** Raw ItemOID -> Value, exactly as exported. */
  items: Record<string, string>;
}

export interface OdmSubjectRecord {
  subjectKey: string;
  studyEventOids: string[];
  forms: OdmFormInstance[];
}

const CHUNK_BYTES = 2 * 1024 * 1024; // 2 MB slices

const NS_WRAPPER_OPEN =
  '<OdmChunk xmlns:redcap="https://projectredcap.org" ' +
  'xmlns:OpenClinica="http://www.openclinica.org/ns/odm_ext_v130/v3.1" ' +
  'xmlns:ds="http://www.w3.org/2000/09/xmldsig#" ' +
  'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">';
const NS_WRAPPER_CLOSE = '</OdmChunk>';

function attr(el: Element, name: string): string | undefined {
  const v = el.getAttribute(name);
  return v === null ? undefined : v;
}

/** Blob.text() with arrayBuffer / FileReader fallbacks for older engines. */
async function readSliceText(slice: Blob): Promise<string> {
  if (typeof (slice as any).text === 'function') return slice.text();
  if (typeof (slice as any).arrayBuffer === 'function') {
    return new TextDecoder('utf-8').decode(await slice.arrayBuffer());
  }
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file slice'));
    reader.readAsText(slice);
  });
}

/**
 * Read the leading part of the file, up to (but excluding) <ClinicalData>, and
 * close it off so `parseOdmXml` can consume it as a standalone metadata
 * document. Avoids loading the clinical payload just to read the FormDefs.
 */
export async function readOdmMetadataHead(file: File): Promise<string> {
  let offset = 0;
  let buffer = '';
  while (offset < file.size) {
    const slice = file.slice(offset, offset + CHUNK_BYTES);
    buffer += await readSliceText(slice);
    offset += CHUNK_BYTES;
    const idx = buffer.indexOf('<ClinicalData');
    if (idx !== -1) {
      let head = buffer.slice(0, idx);
      if (!head.includes('</Study>')) head += '</Study>';
      return head + '</ODM>';
    }
    // Guard: metadata should never exceed a few MB. Bail out rather than
    // silently loading the whole clinical payload into memory.
    if (buffer.length > 40 * 1024 * 1024) break;
  }
  // No ClinicalData section at all — the whole file is metadata.
  return buffer;
}

export interface StreamProgress {
  /** Bytes of the file consumed so far. */
  bytesRead: number;
  totalBytes: number;
  subjectsSeen: number;
}

export interface StreamOptions {
  /** Called for each parsed subject. May be async; the reader awaits it. */
  onSubject: (subject: OdmSubjectRecord) => void | Promise<void>;
  onProgress?: (p: StreamProgress) => void;
  /** Set to true to abort mid-stream. Checked between blocks. */
  shouldStop?: () => boolean;
  /** Emitted for blocks that could not be parsed. */
  onWarning?: (message: string) => void;
}

function parseSubjectBlock(xml: string): OdmSubjectRecord | null {
  const doc = new DOMParser().parseFromString(
    NS_WRAPPER_OPEN + xml + NS_WRAPPER_CLOSE,
    'text/xml',
  );
  if (doc.getElementsByTagName('parsererror').length) return null;
  const sd = doc.getElementsByTagName('SubjectData')[0];
  if (!sd) return null;

  const subjectKey = attr(sd, 'SubjectKey') || '';
  if (!subjectKey) return null;

  const studyEventOids: string[] = [];
  const evEls = sd.getElementsByTagName('StudyEventData');
  for (let i = 0; i < evEls.length; i++) {
    const oid = attr(evEls[i], 'StudyEventOID');
    if (oid && !studyEventOids.includes(oid)) studyEventOids.push(oid);
  }

  const forms: OdmFormInstance[] = [];
  const formEls = sd.getElementsByTagName('FormData');
  for (let i = 0; i < formEls.length; i++) {
    const fd = formEls[i];
    const formOid = attr(fd, 'FormOID');
    if (!formOid) continue;
    const items: Record<string, string> = {};
    const itemEls = fd.getElementsByTagName('ItemData');
    for (let j = 0; j < itemEls.length; j++) {
      const it = itemEls[j];
      const oid = attr(it, 'ItemOID');
      if (!oid) continue;
      items[oid] = attr(it, 'Value') ?? '';
    }
    forms.push({
      formOid,
      repeatKey: attr(fd, 'FormRepeatKey'),
      items,
    });
  }

  return { subjectKey, studyEventOids, forms };
}

/**
 * Stream every <SubjectData> block out of an ODM file.
 * Resolves once the file has been fully consumed or `shouldStop()` returns true.
 */
export async function streamOdmSubjects(
  file: File,
  opts: StreamOptions,
): Promise<{ subjectsSeen: number; stopped: boolean }> {
  const { onSubject, onProgress, shouldStop, onWarning } = opts;
  let offset = 0;
  let buffer = '';
  let subjectsSeen = 0;
  let stopped = false;

  // Skip straight to the clinical section so metadata never enters the buffer.
  let inClinical = false;

  while (offset < file.size) {
    if (shouldStop?.()) {
      stopped = true;
      break;
    }
    const end = Math.min(offset + CHUNK_BYTES, file.size);
    buffer += await readSliceText(file.slice(offset, end));
    offset = end;

    if (!inClinical) {
      const idx = buffer.indexOf('<ClinicalData');
      if (idx === -1) {
        // Keep only a small tail in case the marker straddles a slice boundary.
        buffer = buffer.slice(-64);
        onProgress?.({ bytesRead: offset, totalBytes: file.size, subjectsSeen });
        continue;
      }
      buffer = buffer.slice(idx);
      inClinical = true;
    }

    // Drain every complete SubjectData block currently in the buffer.
    for (;;) {
      if (shouldStop?.()) {
        stopped = true;
        break;
      }
      const start = buffer.indexOf('<SubjectData');
      if (start === -1) {
        buffer = buffer.slice(-64);
        break;
      }
      const tagEnd = buffer.indexOf('>', start);
      if (tagEnd === -1) {
        buffer = buffer.slice(start);
        break;
      }

      let block: string;
      let consumeTo: number;
      if (buffer[tagEnd - 1] === '/') {
        // Self-closing subject (no form data).
        block = buffer.slice(start, tagEnd + 1);
        consumeTo = tagEnd + 1;
      } else {
        const close = buffer.indexOf('</SubjectData>', tagEnd);
        if (close === -1) {
          buffer = buffer.slice(start);
          break;
        }
        consumeTo = close + '</SubjectData>'.length;
        block = buffer.slice(start, consumeTo);
      }

      buffer = buffer.slice(consumeTo);
      const record = parseSubjectBlock(block);
      if (!record) {
        onWarning?.('A SubjectData block could not be parsed and was skipped.');
        continue;
      }
      subjectsSeen++;
      await onSubject(record);
    }

    if (stopped) break;
    onProgress?.({ bytesRead: offset, totalBytes: file.size, subjectsSeen });
    // Yield to the event loop so the UI can repaint between slices.
    await new Promise((r) => setTimeout(r, 0));
  }

  onProgress?.({ bytesRead: Math.min(offset, file.size), totalBytes: file.size, subjectsSeen });
  return { subjectsSeen, stopped };
}
